import React, { FC, useEffect, useState, useContext } from "react";
import { useDropzone } from "react-dropzone";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	SessionType,
	TrainingDrillRequest,
} from "@/features/training/model/Training/TrainingDrill";
import { TrainingKpi } from "@/features/training/model/Training/TrainingKpi";
import {
	CustomDeleteButton,
	KpiCheckboxesGroup,
} from "@/features/training/ui";
import { FormAttachment } from "@/shared/forms";
import SATOSEditorField from "@/shared/forms/SATOSEditorField";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { AlertToast } from "@/shared/notifications/AlertToast";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { CancelActionButton, SaveActionButton } from "@/shared/ui";
import {
	sharedTaskDialogActionsSx,
	sharedTaskDialogContentSx,
	sharedTaskDialogHeaderSx,
	sharedTaskDialogPanelSx,
	sharedTaskDialogPaperSx,
} from "@/styles/sharedTaskDialogStyles";
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormHelperText,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { Field, type FieldProps, Form, Formik } from "formik";
import * as Yup from "yup";

import {
	useMutateCreateTrainingFile,
	useMutateDeleteTrainingDrill,
	useMutateDeleteTrainingDrillFile,
	useMutateUpdateTrainingDrill,
} from "@/service/TrainingMutations";
import {
	useFetchDrill,
	useFetchDrillFolders,
	useFetchTrainingKpis,
	useFetchUserSummariesByCurrentCompany,
} from "@/service/TrainingQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


/* ──────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ──────────────────────────────────────────────────────────────────────────── */

interface EditTrainingDrillProps {
	id: number;
	type: SessionType;
	open: boolean;
	handleClose: () => void;
}

export interface CheckboxState extends TrainingKpi {
	checked: boolean;
}

type FormValues = {
	Name: string;
	Description: string;
	TrainerId: number | "";
	FolderId: number | "";
	AttachmentIds: number[];
	KpiIds: number[];
	Type: SessionType | "";
};

const toArray = <T,>(maybe: unknown): T[] => {
	if (Array.isArray(maybe)) return maybe as T[];
	if (typeof maybe === "object" && maybe != null) {
		const values = maybe as Record<string, unknown>;
		const upperItems = values["Items"];
		if (Array.isArray(upperItems)) return upperItems as T[];
		const lowerItems = values["items"];
		if (Array.isArray(lowerItems)) return lowerItems as T[];
	}
	return [];
};

const ACCEPTED_TYPES = {
	"application/pdf": [".pdf"],
	"application/msword": [".doc"],
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
		".docx",
	],
	"image/jpeg": [".jpg", ".jpeg"],
	"image/png": [".png"],
};
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Section header                                                               */
/* ──────────────────────────────────────────────────────────────────────────── */

const SectionHeader: FC<{
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	icon?: React.ReactNode;
}> = ({ title, subtitle, icon }) => (
	<Box sx={{ mb: 1.5 }}>
		<Stack direction="row" alignItems="center" gap={1}>
			{icon}
			<Typography variant="subtitle1" fontWeight={700} color="text.primary">
				{title}
			</Typography>
		</Stack>
		{subtitle && (
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{ display: "block", mt: 0.25 }}
			>
				{subtitle}
			</Typography>
		)}
	</Box>
);

/* ──────────────────────────────────────────────────────────────────────────── */
/* Dialog shell                                                                 */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditTrainingDrill: FC<EditTrainingDrillProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isExercise = props.type === SessionType.Exercise;
	const closeButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
	};

	return (
		<Dialog
			onClose={props.handleClose}
			open={props.open}
			fullWidth
			maxWidth="md"
			fullScreen={isMobile}
			aria-labelledby="edit-drill-title"
			PaperProps={{
				sx: sharedTaskDialogPaperSx(theme, isMobile),
			}}
		>
			<DialogTitle
				id="edit-drill-title"
				component="div"
				sx={{
					...sharedTaskDialogHeaderSx(theme),
					position: "relative",
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 1.5,
				}}
			>
				<Typography
					variant="caption"
					sx={{
						display: "block",
						fontWeight: 800,
						letterSpacing: "0.08em",
						color: "primary.main",
						mb: 0.4,
					}}
				>
					<Translate String="Auto.Features.Training.Ui.Forms.EditTrainingDrill.L196C6.TRAINING">TRAINING</Translate>
				</Typography>
				<Typography
					variant="h6"
					component="h2"
					sx={{ fontWeight: 700, color: "text.primary", pr: 5, mb: 0.75 }}
				>
					<Translate
						String={
							isExercise
								? "Training.Drills.EditDrill.Exercise.Title"
								: "Training.Drills.EditDrill.Event.Title"
						}
					>
						{isExercise ? "Edit Exercise" : "Edit Event"}
					</Translate>
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ mt: 0.5, display: "block" }}
				>
					<Translate
						String={
							isExercise
								? "Training.Drills.EditDrill.Exercise.Subtitle"
								: "Training.Drills.EditDrill.Event.Subtitle"
						}
					>
						{isExercise
							? "Update details, ownership, attachments, and KPI parameters."
							: "Update event details, ownership, attachments, and KPI parameters."}
					</Translate>
				</Typography>
				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Ui.Forms.EditTrainingDrill.L232C17.Close", "Close")}
					onClick={props.handleClose}
					size="small"
					sx={{ position: "absolute", right: 8, top: 8, ...closeButtonSx }}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent
				sx={{
					...sharedTaskDialogContentSx(theme),
					px: { xs: 2, sm: 2.5 },
					pt: 2,
					pb: 0,
					flex: 1,
					overflow: "auto",
				}}
			>
				<EditTrainingDrillForm {...props} />
			</DialogContent>
		</Dialog>
	);
};

/* ──────────────────────────────────────────────────────────────────────────── */
/* Form                                                                         */
/* ──────────────────────────────────────────────────────────────────────────── */

export const EditTrainingDrillForm: FC<EditTrainingDrillProps> = (props) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const { data: drill, isLoading: isLoadingDrill } = useFetchDrill(props.id);
	const { data: foldersRaw, isLoading: isLoadingFolderOptions } =
		useFetchDrillFolders();
	const { data: trainerRaw, isLoading: isLoadingTrainerOptions } =
		useFetchUserSummariesByCurrentCompany();
	const { data: kpiRaw, isLoading: isLoadingKpiOptions } =
		useFetchTrainingKpis();

	const folders = toArray<{ Id?: number; Name: string }>(foldersRaw);
	const trainerOptions = toArray<{ Id: number; Name: string }>(trainerRaw);
	const kpiOptions = toArray<TrainingKpi>(kpiRaw);

	const [checkboxesState, setCheckboxesState] = useState<
		Record<number, CheckboxState>
	>({});
	const [newAttachmentId, setNewAttachmentId] = useState<number | undefined>();
	const [newAttachment, setNewAttachment] = useState<File | undefined>();

	const updateTrainingDrill = useMutateUpdateTrainingDrill();
	const { createTrainingFile, isSubmitting: isUploadingFile } =
		useMutateCreateTrainingFile();
	const deleteTrainingDrill = useMutateDeleteTrainingDrill();
	const deleteTrainingDrillFile = useMutateDeleteTrainingDrillFile();
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		if (kpiOptions.length && drill) {
			const selectedIds = (drill?.KpiParameters ?? []).map((x) => x.Id);
			const next = kpiOptions.reduce<Record<number, CheckboxState>>(
				(state, current) => {
					state[current.Id] = {
						...current,
						checked: selectedIds.includes(current.Id),
					};
					return state;
				},
				{},
			);
			setCheckboxesState(next);
		}
	}, [kpiOptions, drill]);

	const handleSave = async (request: TrainingDrillRequest) => {
		request.KpiIds = Object.values(checkboxesState)
			.filter((kpi) => kpi.checked)
			.map((kpi) => kpi.Id);

		if (newAttachmentId) {
			request.AttachmentIds = [
				...(request.AttachmentIds ?? []),
				newAttachmentId,
			];
		}

		try {
			await updateTrainingDrill({ drillId: props.id, request });
			props.handleClose();
			void AlertToast.success();
		} catch (error) {
			console.error(error);
			void AlertToast.failed();
		}
	};

	const handleDelete = async () => {
		if (isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			props.handleClose();
			const confirmation = await AlertRequest.delete();
			if (!confirmation.isConfirmed) return;
			await deleteTrainingDrill(props.id);
			AlertRequest.deleteConfirmed(drill?.Name ?? "Training drill");
		} catch (error) {
			console.error(error);
			void AlertRequest.failed();
		} finally {
			setIsDeleting(false);
		}
	};

	const uploadFile = async (file: File) => {
		try {
			const response = await createTrainingFile({ File: file });
			setNewAttachment(file);
			setNewAttachmentId(response.Id);
		} catch (error) {
			console.error(error);
			void AlertToast.failed();
		}
	};

	const handleDeleteFile = async (fileId: number) => {
		try {
			await deleteTrainingDrillFile({ drillId: props.id, fileId });
			void AlertToast.success();
		} catch (error) {
			console.error(error);
			void AlertToast.failed();
		}
	};

	const handleRemovePendingFile = () => {
		setNewAttachment(undefined);
		setNewAttachmentId(undefined);
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: (acceptedFiles: File[]) => {
			const file = acceptedFiles[0];
			if (file) void uploadFile(file);
		},
		accept: ACCEPTED_TYPES,
		maxSize: MAX_FILE_SIZE,
		maxFiles: 1,
		multiple: false,
		disabled: isUploadingFile,
		onDropRejected: (rejections) => {
			const err = rejections[0]?.errors?.[0];
			if (err?.code === "file-too-large") {
				void AlertToast.failed("File exceeds 5 MB limit");
			} else if (err?.code === "file-invalid-type") {
				void AlertToast.failed("Invalid file type. Use PDF, DOC, JPG or PNG.");
			}
		},
	});

	const preventFocusLose = (e: React.KeyboardEvent) => {
		e.stopPropagation();
	};

	if (
		isLoadingDrill ||
		isLoadingFolderOptions ||
		isLoadingTrainerOptions ||
		isLoadingKpiOptions ||
		!drill
	) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<LinearProgress />
			</Box>
		);
	}

	const firstTrainerId = trainerOptions.length ? trainerOptions[0].Id : 0;
	const firstFolderId = folders.length ? (folders[0].Id ?? 0) : 0;

	const validationSchema = Yup.object().shape({
		Name: Yup.string().required("Name is required"),
		Description: Yup.string(),
		TrainerId: Yup.number().required(),
		FolderId: Yup.number().required(),
		AttachmentIds: Yup.array().of(Yup.number()),
		KpiIds: Yup.array().of(Yup.number()),
		Type: Yup.string().required(),
	});
	const sectionCardSx = {
		...sharedTaskDialogPanelSx(theme),
		p: { xs: 2, md: 2.5 },
	};

	return (
		<Formik<FormValues>
			enableReinitialize
			initialValues={{
				Name: drill?.Name ?? "",
				Description: drill?.Description ?? "",
				TrainerId: drill?.Trainer?.Id ?? firstTrainerId,
				FolderId: drill?.FolderId ?? firstFolderId,
				AttachmentIds: (drill?.Attachments ?? []).map((x) => x.Id),
				KpiIds: (drill?.KpiParameters ?? []).map((x) => x.Id),
				Type: drill?.Type ?? "",
			}}
			onSubmit={(values) => {
				const request: TrainingDrillRequest = {
					...values,
					TrainerId: values.TrainerId === "" ? 0 : Number(values.TrainerId),
					FolderId: values.FolderId === "" ? 0 : Number(values.FolderId),
					Type: values.Type === "" ? SessionType.Event : values.Type,
				};
				void handleSave(request);
			}}
			validationSchema={validationSchema}
		>
			{({ isSubmitting }) => (
				<Form>
					<Stack spacing={3}>
						{/* 1. Details */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								icon={
									<SupervisorAccountIcon
										fontSize="small"
										sx={{ color: "text.secondary" }}
									/>
								}
								title={
									<Translate String="Training.Drill.NewDrill.Section.Details">
										Details
									</Translate>
								}
								subtitle={
									<Translate String="Training.Drill.NewDrill.Section.Details.Desc">
										Name, description, trainer and folder.
									</Translate>
								}
							/>
							<Stack spacing={2}>
								<Field name="Name">
									{({ field, meta }: FieldProps<string, FormValues>) => (
										<TextField
											{...field}
											label={
												<Translate String="Training.Drill.NewDrill.Name">
													Name
												</Translate>
											}
											fullWidth
											size="small"
											required
											onKeyDown={preventFocusLose}
											error={meta.touched && Boolean(meta.error)}
											helperText={meta.touched && meta.error}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													bgcolor: "background.paper",
												},
											}}
										/>
									)}
								</Field>
								<SATOSEditorField
									id="description"
									name="Description"
									label="Description"
									disabled={isSubmitting}
									style={{ minWidth: "100%" }}
									imageUploadDisabled={true}
								/>
								<Field name="TrainerId">
									{({
										field,
										meta,
										form,
									}: FieldProps<number | "", FormValues>) => (
										<FormControl
											fullWidth
											size="small"
											error={meta.touched && Boolean(meta.error)}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													bgcolor: "background.paper",
												},
											}}
										>
											<InputLabel>
												<Translate String="Training.Drill.NewDrill.Trainer">
													Trainer
												</Translate>
											</InputLabel>
											<Select
												{...field}
												label={
													<Translate String="Training.Drill.NewDrill.Trainer">
														Trainer
													</Translate>
												}
												value={field.value ?? ""}
												onChange={(e) => {
													void form.setFieldValue(
														"TrainerId",
														e.target.value === "" ? "" : Number(e.target.value),
													);
												}}
												displayEmpty
											>
												<MenuItem value="">
													<em>Select trainer…</em>
												</MenuItem>
												{trainerOptions.map((x) => (
													<MenuItem key={x.Id} value={x.Id}>
														{x.Name}
													</MenuItem>
												))}
											</Select>
											{meta.touched && meta.error && (
												<FormHelperText>{meta.error}</FormHelperText>
											)}
										</FormControl>
									)}
								</Field>
								<Field name="FolderId">
									{({
										field,
										meta,
										form,
									}: FieldProps<number | "", FormValues>) => (
										<FormControl
											fullWidth
											size="small"
											error={meta.touched && Boolean(meta.error)}
											sx={{
												"& .MuiOutlinedInput-root": {
													borderRadius: 2,
													bgcolor: "background.paper",
												},
											}}
										>
											<InputLabel>
												<Translate String="Training.Drill.NewDrill.Folder">
													Folder
												</Translate>
											</InputLabel>
											<Select
												{...field}
												label={
													<Translate String="Training.Drill.NewDrill.Folder">
														Folder
													</Translate>
												}
												value={field.value ?? ""}
												onChange={(e) => {
													void form.setFieldValue(
														"FolderId",
														e.target.value === "" ? "" : Number(e.target.value),
													);
												}}
												displayEmpty
											>
												<MenuItem value="">
													<em>Select folder…</em>
												</MenuItem>
												{folders.map((x) => (
													<MenuItem key={x.Id ?? 0} value={x.Id ?? 0}>
														{x.Name}
													</MenuItem>
												))}
											</Select>
											{meta.touched && meta.error && (
												<FormHelperText>{meta.error}</FormHelperText>
											)}
										</FormControl>
									)}
								</Field>
							</Stack>
						</Paper>

						{/* 2. Attachments */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								icon={
									<FolderOpenIcon
										fontSize="small"
										sx={{ color: "text.secondary" }}
									/>
								}
								title={
									<Translate String="Training.EditCourse.Attachments">
										Attachments
									</Translate>
								}
								subtitle={
									<Translate String="Training.EditCourse.Attachments.Desc">
										Upload reference files or supporting material.
									</Translate>
								}
							/>
							<Stack spacing={2}>
								{(drill?.Attachments ?? []).length > 0 && (
									<Stack direction="row" flexWrap="wrap" gap={1}>
										{(drill?.Attachments ?? []).map((attachment) => (
											<FormAttachment
												key={attachment.Id}
												id={attachment.Id}
												filename={attachment.Filename}
												handleDelete={handleDeleteFile}
											/>
										))}
									</Stack>
								)}
								{(drill?.Attachments ?? []).length === 0 && !newAttachment && (
									<Typography variant="body2" color="text.secondary">
										<Translate String="Training.EditCourse.Attachments.None">
											No attachments yet.
										</Translate>
									</Typography>
								)}
								<Box
									{...getRootProps()}
									sx={{
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										border: "2px dashed",
										borderColor: isDragActive ? "primary.main" : "divider",
										borderRadius: 2,
										py: 3,
										px: 2,
										textAlign: "center",
										cursor: "pointer",
										bgcolor: isDragActive
											? "action.hover"
											: "background.default",
										color: "text.secondary",
										transition: "border-color 0.2s, background-color 0.2s",
										"&:hover": {
											borderColor: "primary.main",
											bgcolor: "action.hover",
										},
									}}
								>
									<input {...getInputProps()} id="edit-drill-attachment" />
									{newAttachment ? (
										<Stack
											direction="row"
											alignItems="center"
											spacing={2}
											sx={{
												p: 2,
												borderRadius: 1,
												bgcolor: "background.paper",
												width: "100%",
												maxWidth: 400,
												border: "1px solid",
												borderColor: "divider",
											}}
										>
											<InsertDriveFileOutlinedIcon
												sx={{ color: "text.secondary", fontSize: 32 }}
											/>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography variant="body2" fontWeight={600} noWrap>
													{newAttachment.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{formatFileSize(newAttachment.size)}
												</Typography>
											</Box>
											<IconButton
												onClick={(e) => {
													e.stopPropagation();
													handleRemovePendingFile();
												}}
												color="error"
												size="small"
												aria-label="Remove file"
											>
												<CloseIcon fontSize="small" />
											</IconButton>
										</Stack>
									) : (
										<>
											<Typography variant="body2" sx={{ mb: 0.5 }}>
												<Translate String="Training.NewTrainingCourse.DropZone.Drag">
													Drag & drop files here
												</Translate>
											</Typography>
											<Typography variant="body2">
												<Translate String="Training.NewTrainingCourse.DropZone.Or">
													or
												</Translate>{" "}
												<Box
													component="span"
													sx={{ textDecoration: "underline", color: "primary.main" }}
												>
													<Translate String="Shift.Event.Choose.File">
														Choose files
													</Translate>
												</Box>
											</Typography>
											<Typography variant="caption" sx={{ mt: 0.5 }}>
												<Translate String="Shift.Event.File.Help">
													PDF, DOC, JPG, PNG · max 5 MB
												</Translate>
											</Typography>
										</>
									)}
								</Box>
							</Stack>
						</Paper>

						{/* 3. KPI Parameters */}
						<Paper variant="outlined" sx={sectionCardSx}>
							<SectionHeader
								title={
									<Translate String="Training.Drills.NewDrill.KpiParameter">
										KPI Parameters
									</Translate>
								}
								subtitle={
									<Translate String="Training.Drills.NewDrill.MaxValue">
										Max Value
									</Translate>
								}
							/>
							<KpiCheckboxesGroup
								checkboxesState={checkboxesState}
								setCheckboxesState={setCheckboxesState}
							/>
						</Paper>
					</Stack>

					{/* Footer */}
					<DialogActions
						sx={{
							...sharedTaskDialogActionsSx(theme, isMobile),
							px: 0,
							pt: 2,
							mt: 3,
							flexWrap: "wrap",
							justifyContent: "space-between",
							gap: 1.5,
							display: "flex",
						}}
					>
						<CustomDeleteButton
							handleDelete={handleDelete}
							loading={isDeleting}
							disabled={isSubmitting || isDeleting}
							sx={{ whiteSpace: "nowrap" }}
							textOverride={
								<Translate String="Training.Common.Button.Delete">
									Delete
								</Translate>
							}
						/>
						<Stack direction="row" spacing={1.5}>
							<CancelActionButton
								onClick={props.handleClose}
								disabled={isSubmitting || isDeleting}
							>
								<Translate String="Procedure.Button.Cancel">Cancel</Translate>
							</CancelActionButton>
							<SaveActionButton
								type="submit"
								loading={isSubmitting}
								loadingText={
									<Translate String="LabDaily.Saving">Saving...</Translate>
								}
								disabled={isSubmitting || isDeleting}
							>
								<Translate String="Training.EditCourse.SaveChanges">
									Save changes
								</Translate>
							</SaveActionButton>
						</Stack>
					</DialogActions>
				</Form>
			)}
		</Formik>
	);
};
